import { Fzf, type FzfResultItem } from "fzf";

// Office grids key off the element that takes space (`form > .assignee`,
// `.join > .join-item`). The native select is hidden inside the wrapper.
const layoutNames = ["assignee", "join-item", "w-full", "flex-1"] as const;

interface FilterWidget {
  element: HTMLElement;
  destroy: () => void;
}

type Enhanced = HTMLSelectElement & { _filterWidget?: FilterWidget };

interface FilterParts {
  root: HTMLElement;
  control: HTMLButtonElement;
  value: HTMLSpanElement;
  panel: HTMLElement;
  search: HTMLInputElement;
  list: HTMLElement;
}

interface FilterSession extends FilterParts {
  hits: FzfResultItem<HTMLOptionElement>[];
  active: number;
  select: HTMLSelectElement;
  pointerDown: (event: PointerEvent) => void;
  followPanel: (event: Event) => void;
}

interface LabelLift {
  label: HTMLLabelElement | null;
  previousFor: string | null;
  selectAt: number;
}

function widgetOf(select: HTMLSelectElement): FilterWidget | undefined {
  return (select as Enhanced)._filterWidget;
}

function copyLayout(from: Element, onto: Element): void {
  for (const name of layoutNames) {
    if (from.classList.contains(name)) onto.classList.add(name);
  }
}

function selectedText(select: HTMLSelectElement): string {
  return select.selectedOptions[0]?.text ?? "";
}

function rankChoices(
  select: HTMLSelectElement,
  query: string,
): FzfResultItem<HTMLOptionElement>[] {
  // Smart-case is fzf's default: a lowercase query is case-insensitive.
  return new Fzf([...select.options], {
    selector: (option) => option.text,
  }).find(query);
}

// fzf positions are the characters that made the row match; gaps stay plain.
function appendMatches(
  row: HTMLElement,
  label: string,
  positions: Set<number>,
): void {
  let index = 0;
  while (index < label.length) {
    const matched = positions.has(index);
    const start = index;
    while (index < label.length && positions.has(index) === matched) index += 1;
    if (!matched) {
      row.append(label.slice(start, index));
      continue;
    }
    const strong = document.createElement("strong");
    strong.textContent = label.slice(start, index);
    row.append(strong);
  }
}

function paintHits(
  list: HTMLElement,
  search: HTMLInputElement,
  hits: FzfResultItem<HTMLOptionElement>[],
  active: number,
): void {
  const rows = hits.map((hit, index) => {
    const row = document.createElement("div");
    row.className =
      index === active
        ? "filter-select-option is-active"
        : "filter-select-option";
    // Hit position changes with the query; the option index does not.
    row.id = `${list.id}-opt-${hit.item.index}`;
    row.dataset.index = String(hit.item.index);
    row.setAttribute("role", "option");
    row.setAttribute("aria-selected", index === active ? "true" : "false");
    if (hit.positions.size === 0) row.textContent = hit.item.text;
    else appendMatches(row, hit.item.text, hit.positions);
    return row;
  });
  list.replaceChildren(...rows);
  const activeRow = rows[active];
  if (activeRow) search.setAttribute("aria-activedescendant", activeRow.id);
  else search.removeAttribute("aria-activedescendant");
}

function buildFilterShell(select: HTMLSelectElement): FilterParts {
  const root = document.createElement("div");
  root.className = "filter-select";
  const control = document.createElement("button");
  control.type = "button";
  control.className = "filter-select-control";
  control.id = `filter-select-${select.id}`;
  control.setAttribute("aria-haspopup", "listbox");
  control.setAttribute("aria-expanded", "false");
  const value = document.createElement("span");
  value.className = "filter-select-value";
  value.textContent = selectedText(select);
  control.append(value);
  const panel = document.createElement("div");
  panel.className = "filter-select-panel";
  panel.hidden = true;
  const search = document.createElement("input");
  search.type = "text";
  search.className = "filter-select-search";
  search.autocomplete = "off";
  search.setAttribute("aria-label", "Filter");
  const list = document.createElement("div");
  list.className = "filter-select-list";
  list.id = `${control.id}-list`;
  list.setAttribute("role", "listbox");
  search.setAttribute("aria-controls", list.id);
  panel.append(search, list);
  root.append(control, panel);
  if (select.disabled) control.disabled = true;
  return { root, control, value, panel, search, list };
}

function hideNativeSelect(select: HTMLSelectElement): void {
  select.classList.add("filter-select-native");
  select.tabIndex = -1;
  select.setAttribute("aria-hidden", "true");
  select.inert = true;
}

function placeFilterRoot(
  select: HTMLSelectElement,
  parts: FilterParts,
): LabelLift {
  select.parentNode!.insertBefore(parts.root, select);
  parts.root.append(select);
  copyLayout(select, parts.root);
  hideNativeSelect(select);
  return liftSelectLabel(select, parts);
}

// Prefix text ("Dim:", "Packaged") has to stay in the same join item as the
// field. The label is removed until destroy so it does not remain a second item.
function liftSelectLabel(
  select: HTMLSelectElement,
  parts: FilterParts,
): LabelLift {
  const label = select.closest("label");
  if (
    !(label instanceof HTMLLabelElement) ||
    !label.classList.contains("select") ||
    parts.root.parentNode !== label ||
    !label.parentNode
  ) {
    return { label: null, previousFor: null, selectAt: 0 };
  }
  const previousFor = label.hasAttribute("for") ? label.htmlFor : null;
  const siblings = [...label.childNodes];
  const selectAt = siblings.indexOf(parts.root);
  for (const node of siblings) {
    if (node !== parts.root) parts.control.insertBefore(node, parts.value);
  }
  label.parentNode.insertBefore(parts.root, label);
  label.remove();
  copyLayout(label, parts.root);
  return { label, previousFor, selectAt };
}

function restoreFilterSelect(
  select: HTMLSelectElement,
  root: HTMLElement,
  control: HTMLButtonElement,
  value: HTMLSpanElement,
  lifted: LabelLift,
): void {
  select.classList.remove("filter-select-native");
  select.removeAttribute("tabindex");
  select.removeAttribute("aria-hidden");
  select.inert = false;
  const label = lifted.label;
  if (!label) {
    if (root.parentNode) root.parentNode.insertBefore(select, root);
    root.remove();
    return;
  }
  if (root.parentNode) root.parentNode.insertBefore(label, root);
  const prefixes = [...control.childNodes].filter((node) => node !== value);
  prefixes.splice(lifted.selectAt, 0, select);
  label.replaceChildren(...prefixes);
  if (lifted.previousFor === null) label.removeAttribute("for");
  else label.htmlFor = lifted.previousFor;
  root.remove();
}

function placeFilterPanel(session: FilterSession): void {
  const box = session.control.getBoundingClientRect();
  const gap = 4;
  const onLowerHalf = box.top + box.height / 2 > window.innerHeight / 2;
  session.panel.style.left = `${box.left}px`;
  session.panel.style.width = `${box.width}px`;
  if (onLowerHalf) {
    session.panel.style.top = "auto";
    session.panel.style.bottom = `${window.innerHeight - box.top + gap}px`;
    return;
  }
  session.panel.style.bottom = "auto";
  session.panel.style.top = `${box.bottom + gap}px`;
}

function pinActiveToCenter(session: FilterSession): void {
  const row = session.list.children[session.active];
  if (!(row instanceof HTMLElement)) return;
  // scrollTop clamps, so a row near either end sits as close to center as it can.
  const offset =
    row.offsetTop - (session.list.clientHeight - row.offsetHeight) / 2;
  session.list.scrollTop = offset;
}

function revealActive(session: FilterSession): void {
  const row = session.list.children[session.active];
  if (!(row instanceof HTMLElement)) return;
  const bottom = row.offsetTop + row.offsetHeight;
  if (row.offsetTop < session.list.scrollTop)
    session.list.scrollTop = row.offsetTop;
  else if (bottom > session.list.scrollTop + session.list.clientHeight) {
    session.list.scrollTop = bottom - session.list.clientHeight;
  }
}

function closeFilter(session: FilterSession, focusControl: boolean): void {
  if (session.panel.hidden) return;
  session.panel.hidden = true;
  session.root.classList.remove("is-open");
  session.control.setAttribute("aria-expanded", "false");
  session.search.removeAttribute("aria-activedescendant");
  document.removeEventListener("pointerdown", session.pointerDown);
  document.removeEventListener("scroll", session.followPanel, true);
  window.removeEventListener("resize", session.followPanel);
  if (!focusControl) return;
  // keyup of the choosing Enter would activate a button focused during keydown.
  setTimeout(() => session.control.focus(), 0);
}

function openFilter(session: FilterSession): void {
  if (session.select.disabled) return;
  session.root.classList.add("is-open");
  session.control.setAttribute("aria-expanded", "true");
  session.search.value = "";
  session.hits = rankChoices(session.select, "");
  const found = session.hits.findIndex(
    (hit) => hit.item.index === session.select.selectedIndex,
  );
  session.active = found >= 0 ? found : 0;
  paintHits(session.list, session.search, session.hits, session.active);
  placeFilterPanel(session);
  session.panel.hidden = false;
  pinActiveToCenter(session);
  document.addEventListener("scroll", session.followPanel, true);
  window.addEventListener("resize", session.followPanel);
  document.addEventListener("pointerdown", session.pointerDown);
  // The search stays in the scroll cell. A normal focus would scroll that cell.
  session.search.focus({ preventScroll: true });
}

function showFilterQuery(session: FilterSession, query: string): void {
  session.hits = rankChoices(session.select, query);
  session.active = session.hits.length === 0 ? -1 : 0;
  paintHits(session.list, session.search, session.hits, session.active);
  pinActiveToCenter(session);
}

function chooseFilter(session: FilterSession, index: number): void {
  const changed = session.select.selectedIndex !== index;
  session.select.selectedIndex = index;
  session.value.textContent = selectedText(session.select);
  closeFilter(session, true);
  if (!changed) return;
  session.select.dispatchEvent(new Event("change", { bubbles: true }));
}

function moveFilter(session: FilterSession, next: number): void {
  const previous = session.list.children[session.active];
  if (previous instanceof HTMLElement) {
    previous.classList.remove("is-active");
    previous.setAttribute("aria-selected", "false");
  }
  session.active = next;
  const row = session.list.children[session.active];
  if (!(row instanceof HTMLElement)) {
    session.search.removeAttribute("aria-activedescendant");
    return;
  }
  row.classList.add("is-active");
  row.setAttribute("aria-selected", "true");
  session.search.setAttribute("aria-activedescendant", row.id);
  revealActive(session);
}

function onFilterKey(event: KeyboardEvent, session: FilterSession): void {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const delta = event.key === "ArrowDown" ? 1 : -1;
    const next = session.active + delta;
    if (next < 0 || next >= session.hits.length) return;
    moveFilter(session, next);
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const hit = session.hits[session.active];
    if (hit) chooseFilter(session, hit.item.index);
    return;
  }
  if (event.key !== "Escape") return;
  event.preventDefault();
  closeFilter(session, true);
}

function onFilterClick(event: Event, session: FilterSession): void {
  const target = event.target;
  const row =
    target instanceof Element ? target.closest(".filter-select-option") : null;
  if (!(row instanceof HTMLElement)) return;
  const index = Number(row.dataset.index);
  if (!Number.isInteger(index)) return;
  chooseFilter(session, index);
}

function onFilterFocusOut(event: FocusEvent, session: FilterSession): void {
  const next = event.relatedTarget;
  // Hiding the panel blurs the search with no next target.
  if (!(next instanceof Node) || session.root.contains(next)) return;
  closeFilter(session, false);
}

function wireFilter(
  select: HTMLSelectElement,
  parts: FilterParts,
  lifted: LabelLift,
): FilterWidget {
  const session: FilterSession = {
    hits: [],
    active: 0,
    select,
    ...parts,
    pointerDown: () => undefined,
    followPanel: () => undefined,
  };
  session.pointerDown = (event) => {
    if (event.target instanceof Node && session.root.contains(event.target))
      return;
    closeFilter(session, false);
  };
  // scroll does not bubble; capture still hears the cell that holds the control.
  session.followPanel = (event) => {
    const target = event.target;
    if (target instanceof Node && session.panel.contains(target)) return;
    placeFilterPanel(session);
  };
  const onChange = () => {
    session.value.textContent = selectedText(select);
  };
  session.control.addEventListener("click", () => {
    if (session.panel.hidden) openFilter(session);
    else closeFilter(session, false);
  });
  session.search.addEventListener("input", () =>
    showFilterQuery(session, session.search.value),
  );
  session.search.addEventListener("keydown", (event) =>
    onFilterKey(event, session),
  );
  // The search sits in the form. Blur fires change when the query differs
  // from focus time, which would autosave a row the select did not change.
  session.search.addEventListener("change", (event) => event.stopPropagation());
  // Cancelling the option press keeps the search focused so click can land.
  // The scrollbar is the list itself; its drag needs the default action.
  session.list.addEventListener("mousedown", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(".filter-select-option")) return;
    event.preventDefault();
  });
  session.list.addEventListener("click", (event) =>
    onFilterClick(event, session),
  );
  session.root.addEventListener("focusout", (event) =>
    onFilterFocusOut(event, session),
  );
  select.addEventListener("change", onChange);
  return {
    element: session.root,
    destroy() {
      closeFilter(session, false);
      select.removeEventListener("change", onChange);
      delete (select as Enhanced)._filterWidget;
      const { root, control, value } = session;
      restoreFilterSelect(select, root, control, value, lifted);
    },
  };
}

function mountFilter(select: HTMLSelectElement): void {
  if (widgetOf(select)) {
    // History-save re-enhances before HTMX settle copies the server class
    // back onto id'd elements, which drops filter-select-native in place.
    hideNativeSelect(select);
    return;
  }
  // Callers promise a unique id; this does not invent one.
  if (!select.id) return;
  if (!select.parentNode) return;
  const parts = buildFilterShell(select);
  const lifted = placeFilterRoot(select, parts);
  (select as Enhanced)._filterWidget = wireFilter(select, parts, lifted);
}

function selectsUnder(root: ParentNode): Iterable<HTMLSelectElement> {
  if (root instanceof HTMLSelectElement) return [root];
  return root.querySelectorAll("select");
}

function enhanceFilters(root: ParentNode): void {
  for (const select of selectsUnder(root)) {
    // filter-native is Any/Yes/No. A type-to-filter list is the wrong control for three fixed choices.
    if (
      select.multiple ||
      select.size > 1 ||
      select.classList.contains("filter-native")
    )
      continue;
    mountFilter(select);
  }
}

function destroyFilterSelect(select: HTMLSelectElement): void {
  const widget = widgetOf(select);
  if (!widget) return;
  // destroy() puts the select back in front of the wrapper. A select that was
  // already removed would reappear and leave the panel behind.
  const removed = !select.isConnected && widget.element.isConnected;
  widget.destroy();
  if (!removed) return;
  const host = select.parentElement;
  if (host instanceof HTMLLabelElement && host.classList.contains("select")) {
    host.remove();
    return;
  }
  select.remove();
}

function releaseFilters(root: ParentNode): void {
  for (const select of selectsUnder(root)) destroyFilterSelect(select);
}

function onBeforeSwap(event: Event): void {
  // 204 and 304 set the swap to none and skip after:settle, so the widgets stay.
  if (!(event instanceof CustomEvent) || event.defaultPrevented) return;
  const detail = event.detail as {
    tasks?: { target?: EventTarget | null; swapSpec?: { style?: string } }[];
  } | null;
  for (const task of detail?.tasks ?? []) {
    if (task.swapSpec?.style === "none") continue;
    if (task.target instanceof Element) releaseFilters(task.target);
  }
}

function forgetRemovedNode(node: Node): void {
  if (!(node instanceof Element)) return;
  for (const select of selectsUnder(node)) {
    const widget = widgetOf(select);
    // Building the widget moves the select into its wrapper. That is not removal.
    if (!widget || (select.isConnected && widget.element.contains(select)))
      continue;
    destroyFilterSelect(select);
  }
}

function forgetRemovedFilters(records: MutationRecord[]): void {
  for (const record of records) {
    for (const node of record.removedNodes) forgetRemovedNode(node);
  }
}

function bootFilters(): void {
  enhanceFilters(document);
  document.addEventListener("htmx:after:settle", () =>
    enhanceFilters(document),
  );
  document.addEventListener("htmx:before:swap", onBeforeSwap);
  document.addEventListener("htmx:before:cleanup", (event) => {
    if (event.target instanceof Element) releaseFilters(event.target);
  });
  const observer = new MutationObserver(forgetRemovedFilters);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", bootFilters);
else bootFilters();
